/** Dialogs de pasta: criar/editar ('Nova pasta' / 'Editar pasta') e excluir. */
import { useState, type FormEvent } from 'react';
import type { KnowledgeFolder, KnowledgeFolderBody } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Input, Label, Textarea } from '@/components/ui/input';
import { Dialog } from '@/components/ui/misc';
import { errorMessage } from '@/features/knowledge/knowledge-queries';
import {
  useCreateFolder,
  useDeleteFolder,
  useUpdateFolder,
} from '@/features/knowledge/knowledge-mutations';

function FolderForm({
  initial,
  pending,
  error,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initial?: KnowledgeFolder;
  pending: boolean;
  error: unknown;
  submitLabel: string;
  onSubmit: (body: KnowledgeFolderBody) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [icon, setIcon] = useState(initial?.icon ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit({
      name: name.trim(),
      icon: icon.trim() || null,
      description: description.trim() || null,
    });
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="folder-name">
          Nome <span className="text-destructive">*</span>
        </Label>
        <Input
          id="folder-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          autoFocus
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="folder-icon">Ícone</Label>
        <Input
          id="folder-icon"
          value={icon}
          onChange={(e) => setIcon(e.target.value)}
          placeholder="📚 emoji"
          className="w-28"
          maxLength={8}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="folder-description">Descrição</Label>
        <Textarea
          id="folder-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      {error ? <p className="text-sm text-destructive">{errorMessage(error)}</p> : null}
      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" size="sm" disabled={pending || !name.trim()}>
          {pending ? 'Salvando…' : submitLabel}
        </Button>
      </div>
    </form>
  );
}

export function NewFolderDialog({ onClose }: { onClose: () => void }) {
  const create = useCreateFolder();
  return (
    <Dialog open onClose={onClose} title="Nova pasta">
      <FolderForm
        pending={create.isPending}
        error={create.error}
        submitLabel="Criar pasta"
        onCancel={onClose}
        onSubmit={(body) => create.mutate(body, { onSuccess: onClose })}
      />
    </Dialog>
  );
}

export function EditFolderDialog({
  folder,
  onClose,
}: {
  folder: KnowledgeFolder;
  onClose: () => void;
}) {
  const update = useUpdateFolder(folder.id);
  return (
    <Dialog open onClose={onClose} title="Editar pasta">
      <FolderForm
        initial={folder}
        pending={update.isPending}
        error={update.error}
        submitLabel="Salvar"
        onCancel={onClose}
        onSubmit={(body) => update.mutate(body, { onSuccess: onClose })}
      />
    </Dialog>
  );
}

export function DeleteFolderDialog({
  folder,
  onClose,
}: {
  folder: KnowledgeFolder;
  onClose: () => void;
}) {
  const remove = useDeleteFolder();
  const notEmpty =
    remove.error !== null &&
    typeof remove.error === 'object' &&
    'error' in remove.error &&
    (remove.error as { error: string }).error === 'folder_not_empty';

  return (
    <Dialog open onClose={onClose} title="Excluir pasta">
      <p className="text-sm text-muted-foreground">
        Tem certeza que deseja excluir a pasta{' '}
        <strong className="text-foreground">{folder.name}</strong>? Essa ação não pode ser
        desfeita.
      </p>
      {remove.error ? (
        <p className="mt-3 text-sm text-destructive">
          {notEmpty
            ? 'A pasta ainda tem conhecimentos. Mova ou exclua os conhecimentos antes de excluir a pasta.'
            : errorMessage(remove.error)}
        </p>
      ) : null}
      <div className="mt-4 flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          type="button"
          variant="destructive"
          size="sm"
          disabled={remove.isPending}
          onClick={() => remove.mutate(folder.id, { onSuccess: onClose })}
        >
          {remove.isPending ? 'Excluindo…' : 'Excluir'}
        </Button>
      </div>
    </Dialog>
  );
}
